import React from 'react';

function PrivacyPolicy() {
  return (
    <div style={styles.wrapper}>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logo}>
          Fit<span style={styles.logoAccent}>thea</span>
        </div>
      </div>

      {/* Content */}
      <div style={styles.container}>

        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.lastUpdated}>
          Last updated: May 2026
        </p>

        <p style={styles.intro}>
          Fitthea ("we", "our", or "us") is committed to
          protecting your privacy. This Privacy Policy explains
          how we collect, use, and safeguard your information
          when you use our fashion discovery platform at
          fitthea.com.
        </p>

        {/* Section 1 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            1. Information We Collect
          </h2>
          <p style={styles.text}>
            We collect the following types of information:
          </p>

          <div style={styles.subSection}>
            <h3 style={styles.subTitle}>
              a) Account Information
            </h3>
            <p style={styles.text}>
              When you create an account, we collect your
              name, email address, and profile photo (if
              signing in via Google).
            </p>
          </div>

          <div style={styles.subSection}>
            <h3 style={styles.subTitle}>
              b) Body Profile Information
            </h3>
            <p style={styles.text}>
              To personalise fashion recommendations, we
              collect your body shape, gender, top size,
              and bottom size. This information is stored
              securely and used only to improve your
              shopping experience.
            </p>
          </div>

          <div style={styles.subSection}>
            <h3 style={styles.subTitle}>
              c) Search Data
            </h3>
            <p style={styles.text}>
              We store your search history to improve
              recommendations and provide a personalised
              experience. Search queries are associated
              with your account when logged in.
            </p>
          </div>

          <div style={styles.subSection}>
            <h3 style={styles.subTitle}>
              d) Usage Data
            </h3>
            <p style={styles.text}>
              We automatically collect information about
              how you interact with Fitthea, including
              pages visited, search terms used, and
              products clicked.
            </p>
          </div>
        </div>

        {/* Section 2 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            2. How We Use Your Information
          </h2>
          <p style={styles.text}>
            We use the information we collect to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              Provide personalised fashion recommendations
              based on your body shape and preferences
            </li>
            <li style={styles.listItem}>
              Improve our AI styling suggestions
            </li>
            <li style={styles.listItem}>
              Show relevant products from partner platforms
              like Tata CLiQ, Myntra, Ajio and Meesho
            </li>
            <li style={styles.listItem}>
              Send you updates about new features
              (only if you opt in)
            </li>
            <li style={styles.listItem}>
              Analyse usage patterns to improve the platform
            </li>
            <li style={styles.listItem}>
              Comply with legal obligations
            </li>
          </ul>
        </div>

        {/* Section 3 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            3. Affiliate Links & Third Party Platforms
          </h2>
          <p style={styles.text}>
            Fitthea uses affiliate links to earn commissions
            when you make purchases on partner platforms.
            When you click a product link, you may be
            redirected to third party websites including
            Tata CLiQ, Myntra, Ajio, and Meesho.
          </p>
          <p style={styles.text}>
            These platforms have their own privacy policies
            and we encourage you to review them. We are not
            responsible for the privacy practices of these
            third party websites.
          </p>
          <p style={styles.text}>
            We use affiliate networks including Cuelinks
            to track purchases made through our links.
            These networks may use cookies to track
            conversions.
          </p>
        </div>

        {/* Section 4 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            4. Data Storage & Security
          </h2>
          <p style={styles.text}>
            Your data is stored securely using Google
            Firebase, which provides industry-standard
            encryption and security measures. We implement
            appropriate technical and organisational measures
            to protect your personal information against
            unauthorised access, alteration, disclosure,
            or destruction.
          </p>
          <p style={styles.text}>
            Your body profile data (shape, size, gender)
            is stored in our secure database and is only
            accessible to you through your account.
          </p>
        </div>

        {/* Section 5 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            5. AI & Body Shape Analysis
          </h2>
          <p style={styles.text}>
            Fitthea uses artificial intelligence to provide
            personalised fashion recommendations based on
            your body shape. Any body measurements or shape
            data you provide is used solely to improve your
            shopping experience and is never shared with
            third parties for advertising purposes.
          </p>
          <p style={styles.text}>
            If you choose to use our AI Body Scan feature
            (coming soon), any images processed are used
            only for body shape detection and are not
            stored permanently on our servers.
          </p>
        </div>

        {/* Section 6 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            6. Cookies
          </h2>
          <p style={styles.text}>
            We use cookies and similar tracking technologies
            to improve your experience on Fitthea. These
            include:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Essential cookies:</strong> Required
              for the platform to function correctly,
              including login sessions
            </li>
            <li style={styles.listItem}>
              <strong>Analytics cookies:</strong> Help us
              understand how users interact with Fitthea
            </li>
            <li style={styles.listItem}>
              <strong>Affiliate cookies:</strong> Used by
              our affiliate partners to track purchases
              made through our links
            </li>
          </ul>
          <p style={styles.text}>
            You can control cookies through your browser
            settings. Disabling certain cookies may affect
            the functionality of Fitthea.
          </p>
        </div>

        {/* Section 7 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            7. Data Sharing
          </h2>
          <p style={styles.text}>
            We do not sell your personal information to
            third parties. We may share your information
            only in the following circumstances:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              With affiliate networks (Cuelinks) for
              purchase tracking purposes
            </li>
            <li style={styles.listItem}>
              With service providers who assist in
              operating our platform (Google Firebase,
              Anthropic Claude AI)
            </li>
            <li style={styles.listItem}>
              When required by law or to protect our
              legal rights
            </li>
          </ul>
        </div>

        {/* Section 8 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            8. Your Rights
          </h2>
          <p style={styles.text}>
            You have the following rights regarding
            your personal data:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Access:</strong> Request a copy of
              the personal data we hold about you
            </li>
            <li style={styles.listItem}>
              <strong>Correction:</strong> Update or
              correct inaccurate personal data
            </li>
            <li style={styles.listItem}>
              <strong>Deletion:</strong> Request deletion
              of your personal data and account
            </li>
            <li style={styles.listItem}>
              <strong>Portability:</strong> Receive your
              data in a portable format
            </li>
            <li style={styles.listItem}>
              <strong>Opt-out:</strong> Unsubscribe from
              marketing communications at any time
            </li>
          </ul>
          <p style={styles.text}>
            To exercise any of these rights, please
            contact us at privacy@fitthea.com
          </p>
        </div>

        {/* Section 9 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            9. Children's Privacy
          </h2>
          <p style={styles.text}>
            Fitthea is not intended for users under the
            age of 13. We do not knowingly collect personal
            information from children under 13. If you
            believe we have inadvertently collected
            information from a child, please contact us
            immediately.
          </p>
        </div>

        {/* Section 10 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            10. Changes to This Policy
          </h2>
          <p style={styles.text}>
            We may update this Privacy Policy from time
            to time. We will notify you of any significant
            changes by updating the "Last updated" date
            at the top of this page. Continued use of
            Fitthea after changes constitutes acceptance
            of the updated policy.
          </p>
        </div>

        {/* Section 11 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            11. Contact Us
          </h2>
          <p style={styles.text}>
            If you have any questions about this Privacy
            Policy or how we handle your data, please
            contact us:
          </p>
          <div style={styles.contactCard}>
            <p style={styles.contactItem}>
              📧 <strong>Email:</strong> privacy@fitthea.com
            </p>
            <p style={styles.contactItem}>
              🌐 <strong>Website:</strong> www.fitthea.com
            </p>
            <p style={styles.contactItem}>
              📍 <strong>Address:</strong> Andhra Pradesh, India
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>
            © 2026 Fitthea. All rights reserved.
          </p>
          <div style={styles.footerLinks}>
            <a href="/" style={styles.footerLink}>
              Home
            </a>
            <span style={styles.dot}>·</span>
            <a href="/privacy" style={styles.footerLink}>
              Privacy Policy
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    background: '#FFF9F4',
  },
  header: {
    padding: '18px 40px',
    background: '#FFF9F4',
    borderBottom: '1px solid #EDE4DA',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  logo: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '22px',
    fontWeight: '700',
    color: '#2C2420',
    letterSpacing: '-0.5px',
  },
  logoAccent: {
    color: '#C8715A',
    fontStyle: 'italic',
  },
  container: {
    maxWidth: '760px',
    margin: '0 auto',
    padding: '48px 40px 80px',
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '40px',
    fontWeight: '700',
    color: '#2C2420',
    marginBottom: '8px',
  },
  lastUpdated: {
    fontSize: '13px',
    color: '#8A7F7A',
    marginBottom: '28px',
  },
  intro: {
    fontSize: '15px',
    color: '#2C2420',
    lineHeight: '1.8',
    marginBottom: '40px',
    padding: '20px 24px',
    background: '#F5EDD8',
    borderRadius: '12px',
    borderLeft: '4px solid #C8715A',
  },
  section: {
    marginBottom: '40px',
    paddingBottom: '40px',
    borderBottom: '1px solid #EDE4DA',
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '20px',
    fontWeight: '700',
    color: '#2C2420',
    marginBottom: '14px',
  },
  subSection: {
    marginTop: '16px',
    paddingLeft: '16px',
    borderLeft: '2px solid #EDE4DA',
  },
  subTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2C2420',
    marginBottom: '6px',
  },
  text: {
    fontSize: '14px',
    color: '#5A5450',
    lineHeight: '1.8',
    marginBottom: '12px',
  },
  list: {
    paddingLeft: '20px',
    margin: '12px 0',
  },
  listItem: {
    fontSize: '14px',
    color: '#5A5450',
    lineHeight: '1.8',
    marginBottom: '8px',
  },
  contactCard: {
    background: 'white',
    border: '1px solid #EDE4DA',
    borderRadius: '12px',
    padding: '20px 24px',
    marginTop: '16px',
  },
  contactItem: {
    fontSize: '14px',
    color: '#2C2420',
    marginBottom: '8px',
    lineHeight: '1.6',
  },
  footer: {
    textAlign: 'center',
    paddingTop: '40px',
  },
  footerText: {
    fontSize: '13px',
    color: '#8A7F7A',
    marginBottom: '8px',
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
  },
  footerLink: {
    fontSize: '13px',
    color: '#C8715A',
    textDecoration: 'none',
  },
  dot: {
    color: '#8A7F7A',
    fontSize: '13px',
  },
};

export default PrivacyPolicy;